import type { WebinarSurveyStats } from "@xop/dashboard-shared";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import PollIcon from "@mui/icons-material/Poll";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import type { JSX } from "react";

import { useCan } from "../../../../auth/permissions.js";
import { toast } from "../../../../components/ui/use-toast.js";
import { downloadBlob, webinarsApi } from "../client.js";
import {
  CampaignStatus,
  DoneNote,
  OpenCampaignButton,
  SafetyNote,
  StepFooter,
  StepIntro,
  type StepProps,
} from "../stepShared.js";

/**
 * Step 4 — ask how it went, and read the answers.
 *
 * The form itself is NOT here. It is published to the customer support site
 * (`support.xopnetworks.com/webinar-survey/<token>`) because the people answering
 * are webinar attendees with no XOP login — a dashboard-hosted form would be
 * unanswerable. This step publishes it, drafts the email that carries the
 * link, and then becomes the stats screen: response rate, per-question
 * averages, and every individual answer.
 */
export function StepSurvey({ detail, refresh, canEdit, markDone }: StepProps): JSX.Element {
  const { webinar, survey, campaigns, attendees } = detail;
  const canReachCampaigns = useCan("crm:view");
  const joined = attendees.filter((a) => a.joined).length;

  const publish = useMutation({
    mutationFn: () => webinarsApi.publishSurvey(webinar.id),
    onSuccess: () => {
      refresh();
      toast({ title: "Survey is live", description: "Anyone with the link can answer it." });
    },
    onError: (e: Error) =>
      toast({ title: "Could not publish", description: e.message, variant: "destructive" }),
  });

  const draftEmail = useMutation({
    mutationFn: () => webinarsApi.createSurveyCampaign(webinar.id),
    onSuccess: (res) => {
      refresh();
      toast({
        title: "Survey email drafted",
        description: `Addressed to the ${res.recipients} people who joined. It is a draft - open it and press Send.`,
      });
    },
    onError: (e: Error) =>
      toast({ title: "Could not draft the email", description: e.message, variant: "destructive" }),
  });

  const exportCsv = async (): Promise<void> => {
    try {
      downloadBlob(await webinarsApi.exportSurvey(webinar.id), "webinar-survey.csv");
    } catch (e) {
      toast({
        title: "Download failed",
        description: (e as Error)?.message ?? "",
        variant: "destructive",
      });
    }
  };

  return (
    <Box>
      <StepIntro>
        Put a short feedback form on our public support site, then email the link to the people
        who joined. You do not need an account to answer it, so everyone can.
      </StepIntro>

      {!survey.published ? (
        <Stack spacing={2} alignItems="flex-start">
          <Button
            variant="contained"
            size="large"
            startIcon={<PollIcon />}
            onClick={() => publish.mutate()}
            disabled={!canEdit || publish.isPending}
            data-testid="publish-survey"
          >
            {publish.isPending ? "Publishing…" : "Put the survey online"}
          </Button>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Five short questions: how it was overall, how useful the content was, how clear the
            speakers were, what to improve, and what to cover next.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          <DoneNote>The survey is live.</DoneNote>

          {survey.public_url && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "surfaceSoft",
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 12,
                  wordBreak: "break-all",
                  flex: 1,
                }}
                data-testid="survey-url"
              >
                {survey.public_url}
              </Box>
              <Button
                size="small"
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={() => {
                  void navigator.clipboard?.writeText(survey.public_url!);
                  toast({ title: "Link copied" });
                }}
              >
                Copy
              </Button>
            </Box>
          )}

          {canReachCampaigns && (
            <>
              {webinar.survey_campaign_id ? (
                <Stack spacing={1.5}>
                  <CampaignStatus summary={campaigns.survey} />
                  <Box>
                    <OpenCampaignButton
                      campaignId={webinar.survey_campaign_id}
                      label="Open the survey email"
                    />
                  </Box>
                </Stack>
              ) : (
                <Box>
                  <Button
                    variant="contained"
                    onClick={() => draftEmail.mutate()}
                    disabled={!canEdit || joined === 0 || draftEmail.isPending}
                    data-testid="draft-survey-email"
                  >
                    {draftEmail.isPending
                      ? "Writing…"
                      : `Write the email that sends this link to the ${joined} who joined`}
                  </Button>
                  {joined === 0 && (
                    <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1 }}>
                      Add the people who joined in step 2 first.
                    </Typography>
                  )}
                </Box>
              )}
              <SafetyNote />
            </>
          )}

          <SurveyResults survey={survey} onExport={() => void exportCsv()} />
        </Stack>
      )}

      <StepFooter
        step="survey"
        detail={detail}
        markDone={markDone}
        canEdit={canEdit}
        {...(survey.published ? {} : { disabledReason: "Put the survey online first." })}
      />
    </Box>
  );
}

function SurveyResults({
  survey,
  onExport,
}: {
  survey: WebinarSurveyStats;
  onExport: () => void;
}): JSX.Element {
  const ratingQuestions = survey.questions.filter((q) => q.type === "rating");
  const textQuestions = survey.questions.filter((q) => q.type === "text");

  return (
    <Card variant="outlined">
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 15 }}>What people said</Typography>
          {survey.responses > 0 && (
            <Button size="small" startIcon={<DownloadIcon />} onClick={onExport}>
              Download as a spreadsheet
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          <Chip
            size="small"
            color={survey.responses > 0 ? "primary" : "default"}
            label={`${survey.responses} ${survey.responses === 1 ? "answer" : "answers"}`}
            data-testid="survey-response-count"
          />
          <Chip size="small" variant="outlined" label={`${survey.invited} invited`} />
          <Chip size="small" variant="outlined" label={`${survey.response_rate}% answered`} />
        </Box>

        {survey.responses === 0 ? (
          <Alert severity="info">
            No answers yet. They will appear here as people fill the form in.
          </Alert>
        ) : (
          <Stack spacing={2.5}>
            {ratingQuestions.map((q) => {
              const avg = survey.averages[q.key] ?? 0;
              return (
                <Box key={q.key} data-testid={`survey-avg-${q.key}`}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography sx={{ fontSize: 14 }}>{q.prompt}</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {avg.toFixed(1)} / 5
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(avg / 5) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                    {[1, 2, 3, 4, 5]
                      .map((n) => `${n}★ ${survey.distribution[q.key]?.[String(n)] ?? 0}`)
                      .join("  ·  ")}
                  </Typography>
                </Box>
              );
            })}

            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
                Every answer ({survey.recent.length})
              </Typography>
              <Box
                sx={{
                  maxHeight: 360,
                  overflow: "auto",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Who</TableCell>
                      {ratingQuestions.map((q) => (
                        <TableCell key={q.key} align="center">
                          {shortLabel(q.prompt)}
                        </TableCell>
                      ))}
                      {textQuestions.map((q) => (
                        <TableCell key={q.key}>{shortLabel(q.prompt)}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {survey.recent.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>
                          {r.submitted_at.slice(0, 10)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {r.respondent_email || <em>anonymous</em>}
                        </TableCell>
                        {ratingQuestions.map((q) => (
                          <TableCell key={q.key} align="center">
                            {r.ratings[q.key] ?? "-"}
                          </TableCell>
                        ))}
                        {textQuestions.map((q) => (
                          <TableCell key={q.key} sx={{ fontSize: 13 }}>
                            {r.answers[q.key] ?? ""}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

/** Column headers have to fit: keep the first few words of the prompt. */
function shortLabel(prompt: string): string {
  const words = prompt.replace(/\?$/, "").split(/\s+/);
  return words.length <= 4 ? words.join(" ") : `${words.slice(0, 4).join(" ")}…`;
}
